import {
  cars as carsData,
  countries as countriesData,
  foreignPlates,
  isValidPlateFNSM,
  useTranslation,
} from '@gimlite/common';
import {
  AutoComplete,
  Badge,
  Col,
  Form,
  Image,
  Input,
  Row,
  TextArea,
  Widget,
  WizardItemType,
  Zone,
} from '@gimlite/ui-v1';
import { DateTime } from 'luxon';
import type { FormInstance } from 'rc-field-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CreateFPSWizardContext } from '../../createFps.wizard';
import './info.step.scss';
import { observer } from 'mobx-react-lite';

export const InfoStep = observer(
  ({
    context,
    validate,
    submit,
  }: WizardItemType.Config.Component<CreateFPSWizardContext>) => {
    const [currentBrand, setCurrentBrand] = useState(null);
    const [lastBrand, setLastBrand] = useState(null);
    const [instanceForm, setInstanceForm] = useState<FormInstance>();
    const { t, lang } = useTranslation();

    const formattedDate = useCallback(
      (date: string) =>
        t(`DATETIME_HUMAN`)
          .replace('[DATE]', DateTime.fromISO(date).toFormat(`DDDD`))
          .replace('[TIME]', DateTime.fromISO(date).toFormat(`TT`)),
      [lang],
    );

    const currentModel = useMemo(
      () =>
        currentBrand && carsData?.[currentBrand]
          ? carsData[currentBrand].map((value) => ({ value, label: value }))
          : [],
      [currentBrand],
    );

    const countriesFormatted = useMemo(() => {
      if (lang)
        return countriesData?.[lang]
          ? countriesData[lang].map(({ label, value }) => ({
              label,
              value: value.ISO2,
            }))
          : [];
      return [];
    }, [lang]);

    console.log('countriesFormatted', { countriesData, countriesFormatted, lang });

    const lpnHelp = useCallback(
      (text: string) => {
        const translatedText = t(text);

        return (
          <li
            dangerouslySetInnerHTML={{
              __html: translatedText,
            }}
          ></li>
        );
      },
      [lang],
    );

    useEffect(() => {
      if (context?.country && lang) {
        submit({
          countryISO:
            countriesData[lang!].find(({ label }) => label === context.country)
              ?.value?.ISO2 || null,
        });
      }
    }, [context?.country, lang]);

    return (
      <Form
        data={{ value: context }}
        config={{ validateOnChange: true }}
        handleEvent={{
          change: (value: any) => {
            if (lastBrand === null || lastBrand !== value.brand)
              setLastBrand(() => value.brand);

            if (lastBrand !== value.brand) {
              setLastBrand(() => value.brand);
              value.model = '';
            }

            submit({ ...value });
          },
          validate: (value: any) => {
            validate(value);
          },
          instance: (instance) => setInstanceForm(instance),
        }}
      >
        <Zone
          config={{
            zones: [
              ['cameraEnter', 'cameraExit'],
              ['form', 'form'],
            ],
            rows: ['minmax(300px, 1fr)', 'min-content'],
            columns: ['1fr', '1fr'],
          }}
        >
          <Zone.Area config={{ area: 'cameraEnter' }}>
            <Widget
              config={{
                full: true,
                title: formattedDate(context?.entry?.date),
                shadow: false,
              }}
            >
              <div className="infoCameraContain">
                <Image
                  data={{
                    src: context?.entry?.camera,
                  }}
                  config={{
                    size: 'full',
                    preview: true,
                  }}
                />
                <Badge
                  className="infoCameraBadge"
                  config={{
                    size: 'xlarge',
                    mode: 'degraded',
                    radius: false,
                    text: t(`entry`),
                    icon: 'faArrowRightToBracketSolid',
                    color: 'success',
                  }}
                ></Badge>
              </div>
            </Widget>
          </Zone.Area>

          <Zone.Area config={{ area: 'cameraExit' }}>
            <Widget
              config={{
                full: true,
                title: formattedDate(context?.exit?.date),
                shadow: false,
              }}
            >
              <div className="infoCameraContain">
                <Image
                  data={{
                    src: context?.exit?.camera,
                  }}
                  config={{
                    size: 'full',
                    preview: true,
                  }}
                />
                <Badge
                  className="infoCameraBadge"
                  config={{
                    size: 'xlarge',
                    mode: 'degraded',
                    radius: false,
                    text: t(`exit`),
                    icon: 'faArrowRightFromBracketSolid',
                    color: 'warn',
                  }}
                ></Badge>
              </div>
            </Widget>
          </Zone.Area>

          <Zone.Area config={{ area: 'form' }}>
            <Widget config={{ full: true, shadow: false }}>
              <Col>
                <Row config={{ wrap: false }}>
                  <Form.Item
                    className="infoFormItem"
                    config={{
                      way: 'vertical',
                      name: 'country',
                      label: t('country'),
                      rules: [{ required: true }],
                    }}
                  >
                    <AutoComplete
                      data={{
                        items: countriesFormatted || [],
                      }}
                      config={{
                        search: {
                          caseSensitive: false,
                          accentSensitive: false,
                          pattern: 'start',
                        },
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className="infoFormItem"
                    config={{
                      way: 'vertical',
                      name: 'lpn',
                      label: `${t('immatriculation')}`,
                      tooltip: (
                        <div className="lpnInfo">
                          <span className="lpnInfoTitle">
                            {t('registrationInputFormats')}
                          </span>
                          <ul className="lpnInfoList">
                            {lpnHelp('INPUT_REGISTRATION_FRENCH_SVI')}
                            {lpnHelp('INPUT_REGISTRATION_FRENCH_FNI')}
                            {lpnHelp('INPUT_REGISTRATION_FOREIGN_FNI')}
                          </ul>
                        </div>
                      ),
                      rules: [
                        { required: true },
                        {
                          validator:
                            context?.countryISO === 'FR'
                              ? isValidPlateFNSM
                              : foreignPlates,
                        },
                      ],
                      validateTrigger: [],
                    }}
                  >
                    <Input
                      config={{
                        placeholder:
                          !context?.countryISO || context?.countryISO === 'FR'
                            ? 'AA-000-BB'
                            : 'GE12345',
                      }}
                      handleEvent={{
                        input: () => {
                          instanceForm?.setFields([
                            {
                              name: 'lpn',
                              errors: [],
                            },
                          ]);
                        },
                        focus: () => {
                          instanceForm?.setFields([
                            {
                              name: 'lpn',
                              errors: [],
                            },
                          ]);
                        },
                        blur: async () => {
                          try {
                            await instanceForm?.validateFields(['lpn']);
                          } catch (error) {}
                        },
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className="infoFormItem"
                    config={{
                      way: 'vertical',
                      name: 'brand',
                      label: t('brand'),
                      rules: [{ required: true }],
                    }}
                  >
                    <AutoComplete
                      data={{
                        items: Object.keys(carsData).map((value) => ({
                          label: value,
                          value,
                        })),
                      }}
                      handleEvent={{
                        input: (value: any) => setCurrentBrand(() => value),
                      }}
                      config={{
                        search: {
                          caseSensitive: false,
                          accentSensitive: false,
                          pattern: 'start',
                        },
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className="infoFormItem"
                    data={{ defaultValue: '' }}
                    config={{
                      way: 'vertical',
                      name: 'model',
                      label: t('model'),
                      rules: [],
                    }}
                  >
                    <AutoComplete
                      data={{
                        items: currentModel,
                      }}
                      config={{
                        search: {
                          caseSensitive: false,
                          accentSensitive: false,
                          pattern: 'start',
                        },
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    className="infoFormItem"
                    data={{ defaultValue: '' }}
                    config={{
                      way: 'vertical',
                      name: 'comment',
                      label: `${t('comment')} (${t('optional').toLowerCase()})`,
                      rules: [],
                    }}
                  >
                    <TextArea config={{ width: 'medium' }} />
                  </Form.Item>
                </Row>
              </Col>
            </Widget>
          </Zone.Area>
        </Zone>
      </Form>
    );
  },
);
